import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import {
  TALL_CARD_CLASS,
  MINI_CARD_CLASS,
  cardBackground,
  LightStripes,
  FULL_ART_BG_URL,
} from "@/components/cards/card-primitives";
import { themeForPokemonTypes, shade } from "@/components/cards/card-theme";
import { useDominantColors } from "@/hooks/use-dominant-colors";
import type { CardTheme } from "@/components/cards/card-theme";
import type { TcgCardData } from "@/components/cards/utils/tcg-card-data";

export interface DesignProps {
  pokeName: string;
  level: number;
  d: TcgCardData;
  /** Full-art backdrop URL; when set, renders behind the design (holo cards). */
  fullArt?: string | undefined;
  /** Holo/foil VFX layers passed through to Shell (rendered below z-10 content). */
  vfx?: React.ReactNode;
}

export const useTheme = (d: TcgCardData): CardTheme => themeForPokemonTypes(d.types);

/** Shared card shell (border, base gradient). Children stack above it. */
export const Shell = ({
  t,
  children,
  style,
  fullArt,
  vfx,
  compact = false,
}: {
  t: CardTheme;
  children: React.ReactNode;
  style?: React.CSSProperties;
  /** Full-art illustration URL; renders the layered full-art backdrop behind children. */
  fullArt?: string | undefined;
  /** Holo/foil VFX; absolute layer above the backdrop, below z-10 content. */
  vfx?: React.ReactNode;
  /** Shorter shell — height follows content (mini cards). */
  compact?: boolean;
}) => (
  <div className={compact ? MINI_CARD_CLASS : TALL_CARD_CLASS} style={{ ...cardBackground(t), ...style }}>
    {fullArt && <FullArtBackdrop art={fullArt} t={t} />}
    {vfx && (
      <div className="z-[1] absolute inset-0 pointer-events-none">{vfx}</div>
    )}
    {children}
  </div>
);

export const Sprite = ({
  pokeName,
  isShiny,
  scale = 2.1,
  className = "",
}: {
  pokeName: string;
  isShiny?: boolean | undefined;
  scale?: number;
  className?: string;
}) => (
  <PokemonSprite
    pokemon={{ base_pokemon_name: pokeName, ...(isShiny ? { is_shiny: true } : {}) }}
    className={className}
    style={{ scale }}
    showShadow
  />
);

/**
 * Layered full-art backdrop matching the original card treatment: cosmic bg
 * (screen), big blurred official art, art-sampled light stripes, and a
 * type-dark legibility gradient. Fills the whole card behind sprite + info.
 */
export const FullArtBackdrop = ({
  art,
  t,
}: {
  art: string;
  t: CardTheme;
}) => {
  const stripeColors = useDominantColors(art, 3);
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${FULL_ART_BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.35,
          mixBlendMode: "screen",
        }}
      />
      {/* Deep layer: same art, mirrored + 2× scale */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${art})`,
          backgroundSize: "420%",
          backgroundPosition: "center 38%",
          backgroundRepeat: "no-repeat",
          transform: "scaleX(-1)",
          filter: "blur(2px) saturate(1.4)",
          opacity: 0.45,
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${art})`,
          backgroundSize: "210%",
          backgroundPosition: "center 38%",
          backgroundRepeat: "no-repeat",
          filter: "blur(1.5px) saturate(1.55)",
          opacity: 0.72,
        }}
      />
      <LightStripes colors={stripeColors} />
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, ${t.dark}66 0%, transparent 42%, ${t.dark}99 100%)`,
        }}
      />
    </>
  );
};

export { shade };
